import { useEffect, useId, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Crown, Users } from 'lucide-react';
import { CPM_CLIPPER_LEADERBOARD } from '@/queries/queries';
import { formatLeaderboardViews } from '@/lib/cpm/formatLeaderboardViews';
import { leaderboardRowDisplay } from '@/lib/cpm/leaderboardRowDisplay';
import { RankPatentBadge } from '@/components/ranks/RankPatentBadge';
import { ClipperLeaderboardAvatar } from '@/components/cpm/ClipperLeaderboardAvatar';
import type { CpmClipperLeaderboardEntry } from '@/components/cpm/CpmClipperLeaderboard';
import { cn } from '@/lib/utils';

type Translate = ReturnType<typeof useTranslation>['t'];

// `.animate-campaign-ticker` moves the track by -25%, so the track holds
// exactly four identical sets: one slide equals one set, and the loop is seamless.
const LOOP_COPIES = 4;
// Seconds each clipper takes to scroll by, so ten names don't race past.
const SECONDS_PER_ENTRY = 4;

function TickerItem({ entry, rank, language, t }: {
  entry: CpmClipperLeaderboardEntry;
  rank: number;
  language: string;
  t: Translate;
}) {
  const views = formatLeaderboardViews(entry.total_views, language);
  const display = leaderboardRowDisplay(entry);
  const tierId = display.isAnonymous ? null : entry.views_tier_id;

  return (
    <li
      aria-label={display.isAnonymous ? t('cpmLeaderboard.anonymousAria', { views }) : undefined}
      className="flex items-center gap-2.5 flex-shrink-0 px-3 py-2 rounded-lg border bg-primary/10 border-primary/20"
    >
      <span className="text-xs font-bold text-primary tabular-nums">
        {t('cpmLeaderboard.rank', { rank })}
      </span>
      {display.isAnonymous ? (
        <ClipperLeaderboardAvatar anonymous anonymousTestId="cpm-leaderboard-anonymous-avatar" size="sm" />
      ) : (
        <ClipperLeaderboardAvatar name={display.name} avatarUrl={display.avatarUrl} size="sm" />
      )}
      <div className="flex flex-col min-w-0">
        <span className="flex items-center gap-1 text-xs font-medium text-foreground whitespace-nowrap">
          {display.isAnonymous ? (
            <span aria-hidden="true" className="blur-sm select-none">
              {t('cpmLeaderboard.anonymousName')}
            </span>
          ) : (
            <span className="max-w-[9rem] truncate">{display.name}</span>
          )}
          {tierId && (
            <RankPatentBadge tierId={tierId} size="sm" showLabel={false} interactive={false} className="[&>span]:h-4 [&>span]:w-4" />
          )}
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground whitespace-nowrap">
          <Users className="w-3 h-3" aria-hidden="true" />
          {t('cpmLeaderboard.views', { views })}
        </span>
      </div>
    </li>
  );
}

function TickerTrack({ entries, language, t }: {
  entries: CpmClipperLeaderboardEntry[];
  language: string;
  t: Translate;
}) {
  const trackRef = useRef<HTMLDivElement>(null);

  // Stop the animation on unmount so Chrome releases the GPU compositing layer
  // right away instead of letting it bleed into the next page of the SPA.
  useEffect(() => {
    const el = trackRef.current;
    return () => {
      if (!el) return;
      el.style.animation = 'none';
      el.style.transform = 'translateX(0)';
    };
  }, []);

  return (
    // overflow-hidden + contain:paint keep the moving layer inside the strip.
    // With reduced motion the loop copies hide and the first set scrolls by hand.
    <div className="order-3 sm:order-2 basis-full sm:basis-0 sm:flex-1 min-w-0 relative overflow-hidden motion-reduce:overflow-x-auto [contain:paint]">
      <div className="absolute left-0 top-0 bottom-0 w-4 sm:w-6 bg-gradient-to-r from-card to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-4 sm:w-6 bg-gradient-to-l from-card to-transparent z-10 pointer-events-none" />
      <div
        ref={trackRef}
        className="animate-campaign-ticker motion-reduce:animate-none flex w-max items-center h-14 sm:h-16"
        style={{ animationDuration: `${Math.max(entries.length, 3) * SECONDS_PER_ENTRY}s` }}
      >
        {Array.from({ length: LOOP_COPIES }, (_, copy) => (
          <ol
            key={copy}
            data-testid="cpm-leaderboard-ticker-set"
            aria-hidden={copy > 0 ? true : undefined}
            className={cn('flex items-center gap-3 pl-4', copy > 0 && 'motion-reduce:hidden')}
          >
            {entries.map((entry, index) => (
              <TickerItem
                key={entry.anonymous || !entry.user_id ? `anon-${index}` : entry.user_id}
                entry={entry}
                rank={index + 1}
                language={language}
                t={t}
              />
            ))}
          </ol>
        ))}
      </div>
    </div>
  );
}

/**
 * Slim, auto-scrolling strip of the public clipper ranking (top 10, in rank
 * order). It takes the place and the look of the old "earn" banner on the
 * community library, so the ranking costs one row instead of a whole card.
 */
export function CpmLeaderboardTicker({ ranksLink = false }: { ranksLink?: boolean }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const { data, loading, error } = useQuery<{ cpmClipperLeaderboard?: CpmClipperLeaderboardEntry[] }>(
    CPM_CLIPPER_LEADERBOARD,
    { errorPolicy: 'all' },
  );

  if (loading && !data) {
    return (
      <div
        data-testid="cpm-leaderboard-ticker-skeleton"
        aria-hidden="true"
        className="h-[6.5rem] sm:h-16 rounded-xl border border-primary/20 bg-card animate-pulse"
      />
    );
  }

  const entries = data?.cpmClipperLeaderboard ?? [];
  if (error || entries.length === 0) return null;

  return (
    <section
      data-testid="cpm-leaderboard-ticker"
      aria-labelledby={titleId}
      className="flex flex-wrap sm:flex-nowrap items-center rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 to-card overflow-hidden isolate"
    >
      <div className="order-1 flex flex-1 sm:flex-none items-center gap-3 min-w-0 px-4 pt-3 sm:pt-0">
        <Crown className="w-4 h-4 text-primary flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <h2 id={titleId} className="text-xs font-bold text-primary leading-tight">
            {t('cpmLeaderboard.title')}
          </h2>
          <p className="text-[11px] text-muted-foreground leading-tight truncate sm:whitespace-normal sm:max-w-[11rem]">
            {t('cpmLeaderboard.tickerSubtitle')}
          </p>
        </div>
      </div>
      {ranksLink && (
        <Link
          to="/rankings"
          className="order-2 sm:order-3 flex items-center gap-1.5 self-stretch px-4 pt-3 sm:pt-0 flex-shrink-0 text-xs font-bold text-primary hover:bg-primary/10 transition-colors whitespace-nowrap"
        >
          {t('cpmLeaderboard.rankingLink')}
          <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
        </Link>
      )}
      <TickerTrack entries={entries} language={i18n.language} t={t} />
    </section>
  );
}
